/**
 * 학습 기록 캐시 Context
 * - 뷰어 로드 시 즉시 preload하여 캐시에 저장
 * - annotation 저장, 채팅 전송 등 이벤트 시에만 invalidate → 새 데이터만 병합
 */

import React, { createContext, useContext, useCallback, useState, useRef, useEffect, useMemo } from 'react';
import { learningRecordService, LearningRecordData } from '../services/learningRecordService';
import { sharedFolderService, SharedFolderData } from '../services/sharedFolderService';
import apiClient from '../../core/http/axios';

const CACHE_LIMIT = 50;
const LEARNING_RECORDS_INVALIDATE = 'learningRecordsInvalidate';
const INVALIDATE_DEBOUNCE_MS = 800;

export interface LearningRecordsInvalidateDetail {
  bookUuid: string;
  immediate?: boolean;
}

export interface LearningRecordTag {
  label: string;
  content: string;
}

export interface LearningRecordDisplay {
  title: string;
  date: string;
  tags: LearningRecordTag[];
  pageNo: number | null;
  pageDisplayNumber: number | null;
  pageIdx: number | null;
  recordType: string;
  color: string;
  iconLabel: string;
  summary: string;
  annotationType?: string;
  subject?: string;
}

export interface RecordWithOriginal {
  record: LearningRecordDisplay;
  originalData: LearningRecordData;
}

export interface LearningRecordsCacheEntry {
  records: LearningRecordDisplay[];
  recordsWithOriginalData: RecordWithOriginal[];
  folders: SharedFolderData[];
  sharedFolderRecordUuids: Set<string>;
  total: number;
  offset: number;
  hasMore: boolean;
  loading: boolean;
  loadingMore: boolean;
  error: string | null;
}

const getRecordTypeConfig = (recordType: string): { color: string; iconLabel: string; label: string } => {
  switch (recordType) {
    case 'AUDIO': return { color: '#DA0D0D', iconLabel: '녹음', label: '녹음' };
    case 'ANNOTATION': return { color: '#FFBF00', iconLabel: '마킹', label: '마킹' };
    case 'NOTE': return { color: '#25E921', iconLabel: '노트', label: '노트' };
    case 'DIALOG':
    case 'QUIZ':
    case 'CHAT': return { color: '#B10DDA', iconLabel: 'AI', label: 'AI' };
    default: return { color: '#FB9701', iconLabel: '기타', label: '기타' };
  }
};

const formatDate = (dateString: string): string => {
  try {
    const date = new Date(dateString);
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    return `${year}.${month}.${day}`;
  } catch {
    return '';
  }
};

function transformRecords(
  data: LearningRecordData[],
  annotationMap?: Map<string, any>
): LearningRecordDisplay[] {
  return data.map((record: LearningRecordData) => {
    const date = formatDate(record.created_at);
    const config = getRecordTypeConfig(record.record_type);
    const tags: LearningRecordTag[] = [];
    if (record.summary && record.record_type !== 'ANNOTATION') {
      tags.push({ label: '요약', content: record.summary });
    }

    let title = record.title || '';
    let typeLabel = config.label;
    let iconLabel = config.iconLabel;
    let color = config.color;
    let annotationData: any = null;
    if (record.record_type === 'ANNOTATION' && record.annotation_uuid && annotationMap) {
      annotationData = annotationMap.get(record.annotation_uuid);
    }

    if (record.record_type === 'ANNOTATION') {
      const annotationType = annotationData?.type || record.data?.type || '';
      const annotationTitle = annotationData?.data?.title || '';
      const annotationSubject = annotationData?.data?.subject || '';

      if (annotationType === 'MEMO') {
        typeLabel = '메모';
        iconLabel = '메모';
        color = '#091EBB';
        title = annotationTitle || record.title || '';
        title = title || `제목 없는 ${typeLabel}`;
      } else if (annotationType === 'UNDERLINE') {
        typeLabel = '밑줄';
        iconLabel = '밑줄';
        color = '#FB9701';
        title = annotationTitle || record.title || '';
        title = title || `제목 없는 ${typeLabel}`;
      } else if (annotationType === 'HIGHLIGHT') {
        typeLabel = '강조';
        iconLabel = '강조';
        color = '#FFBF00';
        title = annotationTitle || record.title || '';
        title = title || `제목 없는 ${typeLabel}`;
      } else {
        typeLabel = '마킹';
        iconLabel = '마킹';
        color = '#FB9701';
        title = annotationTitle || record.title || '';
        title = title || `제목 없는 ${typeLabel}`;
      }
    } else {
      title = title || `제목 없는 ${typeLabel}`;
    }

    const pageDisplayNumber = record.page_display_number !== null ? record.page_display_number : null;
    const pageIdx = record.page_idx !== null ? record.page_idx : null;
    let annotationType: string | undefined;
    let subject: string | undefined;
    if (record.record_type === 'ANNOTATION') {
      annotationType = annotationData?.type || record.data?.type;
      subject = annotationData?.data?.subject || record.data?.subject;
    }
    let finalSummary = record.summary || '';
    if (record.record_type === 'ANNOTATION' && subject && finalSummary && finalSummary.trim() === subject.trim()) {
      finalSummary = '';
    }

    return {
      title,
      date,
      tags,
      pageNo: null,
      pageDisplayNumber,
      pageIdx,
      recordType: record.record_type,
      color,
      iconLabel,
      summary: finalSummary,
      annotationType,
      subject,
    };
  });
}

async function fetchLearningRecordsData(
  bookUuid: string,
  limit: number,
  offset: number
): Promise<{
  records: LearningRecordDisplay[];
  recordsWithOriginalData: RecordWithOriginal[];
  total: number;
  hasMore: boolean;
  folders: SharedFolderData[];
  sharedFolderRecordUuids: Set<string>;
}> {
  const cacheBustHeaders = {
    'Cache-Control': 'no-cache',
    'Pragma': 'no-cache',
  };

  const [folderResult, recordsResult] = await Promise.all([
    (async () => {
      const folderList = await sharedFolderService.getSharedFolders(bookUuid);
      let recordUuidSet = new Set<string>();
      if (folderList.length > 0) {
        const allRecordUuids = await Promise.all(
          folderList.map((folder) =>
            sharedFolderService.getFolderItems(folder.folder_uuid).then((res) =>
              res.items.filter((i) => i.record?.record_uuid).map((i) => i.record!.record_uuid)
            ).catch(() => [] as string[])
          )
        );
        allRecordUuids.flat().forEach((uuid) => recordUuidSet.add(uuid));
      }
      return { folders: folderList, sharedFolderRecordUuids: recordUuidSet };
    })(),
    learningRecordService.getLearningRecords(bookUuid, limit, offset, cacheBustHeaders),
  ]);

  const data = recordsResult.items.filter((r) => r.record_type !== 'SHARED_FOLDER');
  const annotationRecords = data.filter(
    (r) => r.record_type === 'ANNOTATION' && r.annotation_uuid && r.page_no != null
  );

  let annotationMap = new Map<string, any>();
  if (annotationRecords.length > 0) {
    const pageNos = annotationRecords.map((r) => r.page_no!).filter((v, i, a) => a.indexOf(v) === i).sort((a, b) => a - b);
    try {
      const annRes = await apiClient.get(`/viewer/books/${bookUuid}/annotations`, {
        params: { start_page: pageNos[0], end_page: pageNos[pageNos.length - 1] },
        headers: { accept: 'application/json', ...cacheBustHeaders },
      });
      (annRes.data?.data?.annotations || []).forEach((item: any) => {
        const a = item.annotation;
        if (a?.annotation_uuid) {
          annotationMap.set(a.annotation_uuid, {
            type: a.type,
            data: { title: a.data?.title, subject: a.data?.subject, color: a.data?.color, border_color: a.data?.border_color },
          });
        }
      });
    } catch {}
  }

  const transformedRecords = transformRecords(data, annotationMap);
  const recordsWithData = transformedRecords.map((r, i) => ({ record: r, originalData: data[i] }));
  recordsWithData.sort((a, b) => new Date(b.originalData.created_at).getTime() - new Date(a.originalData.created_at).getTime());
  const filtered = recordsWithData.filter((item) => !folderResult.sharedFolderRecordUuids.has(item.originalData.record_uuid));
  const uniqueRecordsWithData = filtered.filter(
    (item, i, self) => i === self.findIndex((o) => o.originalData.record_uuid === item.originalData.record_uuid)
  );

  return {
    records: uniqueRecordsWithData.map((i) => i.record),
    recordsWithOriginalData: uniqueRecordsWithData,
    total: recordsResult.total,
    hasMore: data.length < recordsResult.total,
    folders: folderResult.folders,
    sharedFolderRecordUuids: folderResult.sharedFolderRecordUuids,
  };
}

interface LearningRecordsCacheContextValue {
  getCache: (bookUuid: string) => LearningRecordsCacheEntry | null;
  preload: (bookUuid: string) => void;
  invalidate: (bookUuid: string) => void;
  loadMore: (bookUuid: string) => Promise<void>;
  addToSharedFolderUuids: (bookUuid: string, recordUuid: string) => void;
  removeFromSharedFolderUuids: (bookUuid: string, recordUuid: string) => void;
}

const LearningRecordsCacheContext = createContext<LearningRecordsCacheContextValue | undefined>(undefined);

export const LearningRecordsCacheProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [cache, setCache] = useState<Map<string, LearningRecordsCacheEntry>>(new Map());
  const preloadingRef = useRef<Set<string>>(new Set());
  const invalidatingRef = useRef<Set<string>>(new Set());
  const invalidateTimerRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());

  const updateCache = useCallback(
    (bookUuid: string, updater: (prev: LearningRecordsCacheEntry | null) => LearningRecordsCacheEntry) => {
      setCache((prev) => {
        const next = new Map(prev);
        const current = next.get(bookUuid) || null;
        next.set(bookUuid, updater(current));
        return next;
      });
    },
    []
  );

  const preload = useCallback((bookUuid: string) => {
    if (!bookUuid || preloadingRef.current.has(bookUuid)) return;

    preloadingRef.current.add(bookUuid);
    updateCache(bookUuid, () => ({
      records: [],
      recordsWithOriginalData: [],
      folders: [],
      sharedFolderRecordUuids: new Set(),
      total: 0,
      offset: 0,
      hasMore: true,
      loading: true,
      loadingMore: false,
      error: null,
    }));

    fetchLearningRecordsData(bookUuid, CACHE_LIMIT, 0)
      .then((result) => {
        updateCache(bookUuid, () => ({
          records: result.records,
          recordsWithOriginalData: result.recordsWithOriginalData,
          folders: result.folders,
          sharedFolderRecordUuids: result.sharedFolderRecordUuids,
          total: result.total,
          offset: result.records.length,
          hasMore: result.hasMore,
          loading: false,
          loadingMore: false,
          error: null,
        }));
      })
      .catch((err) => {
        updateCache(bookUuid, (prev) => ({
          ...(prev || {
            records: [],
            recordsWithOriginalData: [],
            folders: [],
            sharedFolderRecordUuids: new Set(),
            total: 0,
            offset: 0,
            hasMore: true,
            loadingMore: false,
          }),
          loading: false,
          loadingMore: false,
          error: err?.message || '학습 기록을 불러오는데 실패했습니다.',
        }));
      })
      .finally(() => {
        preloadingRef.current.delete(bookUuid);
      });
  }, [updateCache]);

  const doInvalidateFetch = useCallback((bookUuid: string) => {
    invalidatingRef.current.add(bookUuid);

    fetchLearningRecordsData(bookUuid, CACHE_LIMIT, 0)
      .then((result) => {
        updateCache(bookUuid, (prev) => ({
          records: result.records,
          recordsWithOriginalData: result.recordsWithOriginalData,
          folders: result.folders,
          sharedFolderRecordUuids: result.sharedFolderRecordUuids,
          total: result.total,
          offset: result.records.length,
          hasMore: result.hasMore,
          loading: false,
          loadingMore: false,
          error: null,
        }));
      })
      .catch((err) => {
        // console.error('[LearningRecordsCache] invalidate fetch 실패:', err);
      })
      .finally(() => {
        invalidatingRef.current.delete(bookUuid);
      });
  }, [updateCache]);

  const invalidate = useCallback((bookUuid: string) => {
    if (!bookUuid) return;

    const existingTimer = invalidateTimerRef.current.get(bookUuid);
    if (existingTimer) {
      clearTimeout(existingTimer);
    }

    const timer = setTimeout(() => {
      invalidateTimerRef.current.delete(bookUuid);
      doInvalidateFetch(bookUuid);
    }, INVALIDATE_DEBOUNCE_MS);

    invalidateTimerRef.current.set(bookUuid, timer);
  }, [doInvalidateFetch]);

  const loadMore = useCallback(
    async (bookUuid: string) => {
      const entry = cache.get(bookUuid);
      if (!entry || !entry.hasMore || entry.loading || entry.loadingMore || entry.recordsWithOriginalData.length >= entry.total) return;

      setCache((prev) => {
        const e = prev.get(bookUuid);
        if (!e) return prev;
        const next = new Map(prev);
        next.set(bookUuid, { ...e, loadingMore: true });
        return next;
      });

      fetchLearningRecordsData(bookUuid, CACHE_LIMIT, entry.offset)
        .then((result) => {
          setCache((prev) => {
            const e = prev.get(bookUuid);
            if (!e) return prev;
            const existingUuids = new Set(e.recordsWithOriginalData.map((i) => i.originalData.record_uuid));
            const newItems = result.recordsWithOriginalData.filter(
              (i) => !existingUuids.has(i.originalData.record_uuid) && !e.sharedFolderRecordUuids.has(i.originalData.record_uuid)
            );
            const next = new Map(prev);
            if (newItems.length === 0) {
              next.set(bookUuid, {
                ...e,
                offset: e.offset + result.records.length,
                hasMore: false,
                loadingMore: false,
              });
            } else {
              const merged = [...e.recordsWithOriginalData, ...newItems];
              merged.sort((a, b) => new Date(b.originalData.created_at).getTime() - new Date(a.originalData.created_at).getTime());
              next.set(bookUuid, {
                ...e,
                records: merged.map((i) => i.record),
                recordsWithOriginalData: merged,
                offset: e.offset + result.records.length,
                hasMore: result.hasMore,
                loadingMore: false,
              });
            }
            return next;
          });
        })
        .catch(() => {
          setCache((prev) => {
            const e = prev.get(bookUuid);
            if (!e) return prev;
            const next = new Map(prev);
            next.set(bookUuid, { ...e, loadingMore: false });
            return next;
          });
        });
    },
    [cache]
  );

  useEffect(() => {
    const handler = (e: CustomEvent<LearningRecordsInvalidateDetail>) => {
      const { bookUuid, immediate } = e.detail || {};
      if (!bookUuid) return;
      if (immediate) {
        const existingTimer = invalidateTimerRef.current.get(bookUuid);
        if (existingTimer) {
          clearTimeout(existingTimer);
          invalidateTimerRef.current.delete(bookUuid);
        }
        doInvalidateFetch(bookUuid);
      } else {
        invalidate(bookUuid);
      }
    };
    window.addEventListener(LEARNING_RECORDS_INVALIDATE, handler as EventListener);
    return () => {
      window.removeEventListener(LEARNING_RECORDS_INVALIDATE, handler as EventListener);
      invalidateTimerRef.current.forEach((timer) => clearTimeout(timer));
      invalidateTimerRef.current.clear();
    };
  }, [invalidate, doInvalidateFetch]);

  const getCache = useCallback((bookUuid: string) => cache.get(bookUuid) ?? null, [cache]);

  const addToSharedFolderUuids = useCallback((bookUuid: string, recordUuid: string) => {
    setCache((prev) => {
      const entry = prev.get(bookUuid);
      if (!entry) return prev;
      const next = new Map(prev);
      const newSet = new Set(entry.sharedFolderRecordUuids);
      newSet.add(recordUuid);
      next.set(bookUuid, { ...entry, sharedFolderRecordUuids: newSet });
      return next;
    });
  }, []);

  const removeFromSharedFolderUuids = useCallback((bookUuid: string, recordUuid: string) => {
    setCache((prev) => {
      const entry = prev.get(bookUuid);
      if (!entry) return prev;
      const next = new Map(prev);
      const newSet = new Set(entry.sharedFolderRecordUuids);
      newSet.delete(recordUuid);
      next.set(bookUuid, { ...entry, sharedFolderRecordUuids: newSet });
      return next;
    });
  }, []);

  const value = useMemo<LearningRecordsCacheContextValue>(() => ({
    getCache,
    preload,
    invalidate,
    loadMore,
    addToSharedFolderUuids,
    removeFromSharedFolderUuids,
  }), [getCache, preload, invalidate, loadMore, addToSharedFolderUuids, removeFromSharedFolderUuids]);

  return (
    <LearningRecordsCacheContext.Provider value={value}>
      {children}
    </LearningRecordsCacheContext.Provider>
  );
};

export const useLearningRecordsCache = () => {
  const ctx = useContext(LearningRecordsCacheContext);
  return ctx;
};

export function dispatchLearningRecordsInvalidate(bookUuid: string, immediate?: boolean) {
  window.dispatchEvent(new CustomEvent(LEARNING_RECORDS_INVALIDATE, { detail: { bookUuid, immediate } }));
}
